import React, { useCallback, useState } from "react";
import { Button, Drawer, Empty, Spin, Table, Tag, Tooltip } from "antd";
import { HistoryOutlined, InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import lib from "../../assets/utils/lib";
import { formatPaDuration } from "./paDisplayUtils";
import "./pa-turnaround.css";

/**
 * Every PA processing batch on this encounter (PA Turnaround Time Requirements
 * §3 and review §2): the batch time and staff member, then per item its
 * Received time (the facility's submission of that item), Processed time,
 * Turnaround and decision. A reprocessed item appears again in its newer batch;
 * the earlier row is kept. All times are Africa/Lagos.
 */

const SOURCE_LABELS = {
  cs_batch: null,
  cs_single: "Single item",
  system: "Automatic (not counted)",
  manual: "Manual PA (not counted)",
  backfill: "From earlier records",
  backfill_system: "From earlier records (not counted)",
};

const SOURCE_HELP = {
  backfill:
    "Rebuilt from the decision already saved on each item before processing history existed. New decisions create their own records.",
  backfill_system:
    "Rebuilt from earlier records. Decided without a Customer Support staff member, so not counted in turnaround.",
  system: "Approved automatically by the encounter type's policy. Not counted in turnaround.",
  manual: "Created already approved by Customer Support. Not counted in turnaround.",
};

const fmt = (value) => (value ? dayjs(value).format("D MMM YYYY, HH:mm") : "—");

const columnsFor = (batch) => [
  {
    title: "Item",
    dataIndex: "item_name",
    key: "item",
    ellipsis: true,
    render: (name, it) => (
      <span title={name}>
        {name}
        {it.reprocessed ? (
          <Tooltip title="Reprocessed: an earlier decision on this item is in an older batch. Only the first decision counts toward turnaround.">
            <Tag style={{ marginInlineStart: 6 }}>Reprocessed</Tag>
          </Tooltip>
        ) : null}
      </span>
    ),
  },
  { title: "Received", dataIndex: "received_at", key: "received", width: 140, render: fmt },
  {
    title: "Processed",
    dataIndex: "processed_at",
    key: "processed",
    width: 160,
    render: (v, it) => (
      <>
        {fmt(v)}
        {it.processed_by && it.processed_by !== batch.staff_name ? (
          <div className="pa-history__who">by {it.processed_by}</div>
        ) : null}
      </>
    ),
  },
  {
    title: "Turnaround",
    dataIndex: "tat_seconds",
    key: "tat",
    width: 100,
    render: (v, it) =>
      v == null ? (
        <Tooltip title={it.received_at ? "Processed time is earlier than the received time, so this cannot be measured." : "No received time on this item."}>
          <span>Not measurable</span>
        </Tooltip>
      ) : (
        formatPaDuration(v)
      ),
  },
  {
    title: "Status",
    dataIndex: "decision",
    key: "status",
    width: 96,
    render: (d) => (
      <Tag color={d === "approve" ? "green" : "red"} style={{ marginInlineEnd: 0 }}>
        {d === "approve" ? "Approved" : "Rejected"}
      </Tag>
    ),
  },
];

export default function PAProcessingHistory({ checkinId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState(null);

  const load = useCallback(async () => {
    if (!checkinId) return;
    setLoading(true);
    const res = await lib.paProcessingHistory(checkinId);
    setBatches(res?.error ? [] : res?.data?.data?.batches || []);
    setLoading(false);
  }, [checkinId]);

  const show = () => {
    setOpen(true);
    load();
  };

  return (
    <>
      <Button type="default" icon={<HistoryOutlined />} onClick={show} disabled={!checkinId}>
        Processing history
      </Button>
      <Drawer title="PA processing history" open={open} onClose={() => setOpen(false)} width={760} destroyOnClose>
        <p className="pa-history__who" style={{ marginTop: 0 }}>
          Each batch of decisions submitted on this encounter, newest first. Turnaround runs from when the facility
          submitted each item (Received) to when its decision was submitted (Processed). Times are Africa/Lagos.
        </p>
        {loading ? (
          <Spin />
        ) : !batches?.length ? (
          <Empty description="No decisions recorded yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          batches.map((b) => (
            <div key={b.id} className="pa-history__batch">
              <div className="pa-history__head">
                <span className="pa-history__when">{fmt(b.processed_at)}</span>
                <span className="pa-history__who">
                  {b.account_recorded === false && b.staff_name === "Account not recorded" ? (
                    <Tooltip title="This earlier decision was saved without the account that made it, and the activity log has no record of it either. New decisions always record the account.">
                      <span>Account not recorded</span>
                    </Tooltip>
                  ) : (
                    b.staff_name || "Account not recorded"
                  )}
                  {SOURCE_LABELS[b.source] ? ` · ${SOURCE_LABELS[b.source]}` : ""}
                  {SOURCE_HELP[b.source] ? (
                    <Tooltip title={SOURCE_HELP[b.source]}>
                      <InfoCircleOutlined style={{ marginInlineStart: 4 }} aria-label="About this batch" />
                    </Tooltip>
                  ) : null}
                  {` · ${b.item_count} item${b.item_count === 1 ? "" : "s"}`}
                </span>
              </div>
              <div className="pa-tat__table-wrap">
                <Table
                  size="small"
                  rowKey={(it) => `${b.id}-${it.item_id}`}
                  columns={columnsFor(b)}
                  dataSource={b.items}
                  pagination={false}
                  scroll={{ x: 640 }}
                />
              </div>
            </div>
          ))
        )}
      </Drawer>
    </>
  );
}
